import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, FormControl, IconButton, Text} from '@primer/react'
import {useId} from 'react'

import type {BypassActor, SupportedBypassMode} from '../../bypass-actors-types'
import {bypassModeToShortDesc, bypassModeToText} from '../../helpers/constants'
import {filterSupportedModes} from '../../helpers/filter-supported-modes'

import styles from './BypassListRow.module.css'

/**
 * Renders the current mode label and a kebab menu for switching the mode. Does NOT render any deletion controls.
 * When used inside `ActorListEditor`, the editor owns the delete control.
 */
export function BypassModeEditor({
  actor,
  readOnly,
  updateBypassActor,
  supportedBypassModes,
}: {
  actor: BypassActor
  readOnly?: boolean
  updateBypassActor: (bypassActor: BypassActor) => void
  supportedBypassModes: SupportedBypassMode[]
}) {
  const modeLabelId = useId()
  const supportedModes = filterSupportedModes(actor.actorType, supportedBypassModes)
  const showSelector = supportedModes.length > 1
  const label = bypassModeToShortDesc[actor.bypassMode] ?? actor.bypassMode

  if (readOnly) {
    return <>{label}</>
  }

  if (!showSelector) {
    return <Text className={styles.Text}>{label}</Text>
  }

  return (
    <>
      <Text className={styles.Text} id={modeLabelId}>
        {label}
      </Text>
      <FormControl>
        <FormControl.Label visuallyHidden>Select bypass mode</FormControl.Label>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton
              icon={KebabHorizontalIcon}
              variant="invisible"
              aria-label={`${actor.name ?? 'Unknown'} Bypass actor actions`}
              aria-describedby={modeLabelId}
              description="Bypass actor actions"
              className={styles.IconButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay width="xlarge">
            <ActionList selectionVariant="single">
              {supportedModes.map(option => (
                <ActionList.Item
                  key={option.mode}
                  selected={actor.bypassMode === option.mode}
                  onSelect={() => {
                    updateBypassActor({...actor, bypassMode: option.mode, _dirty: true})
                  }}
                >
                  <div className={styles.ActionListItemText}>{bypassModeToText[option.mode]}</div>
                  <ActionList.Description variant="block">{option.description}</ActionList.Description>
                </ActionList.Item>
              ))}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </FormControl>
    </>
  )
}
